import { FC, Fragment, useMemo, useState } from "react"
import {
  Check,
  ChevronDown,
  ClipboardCopy,
  ExternalLink,
  Eye,
  MessageSquare,
  Pencil
} from "lucide-react"
import { Listbox, Transition } from "@headlessui/react"
import { openInNewTab } from "../../../../utils/downloadFile"

import BreadCrumbs from "../../../breadcrumbs"
import { LightBtn } from "../../../buttons"
import Dropdown from "../../../dropdown"
import BBSwitch from "../../../form/Switch"
import ProfileLogo from "../../../profile-logo"
import If from "../../../if"
import Badge from "../../../badge"
import EngagementsStatus from "../../table/cells/engagements/engagements-status"
import {
  useGetThreadQuery,
  useLeadSmsFollowupMutation
} from "../../../../../redux/queries"
import { pushToast } from "../../../../../redux/slices/notifications"
import { useAppDispatch, useInTabletView } from "../../../../hooks"
import { copyToClipboard, getStatusID } from "../../../../utils"
import { STATUS_OPTIONS } from "../../../../constants"
import {
  DropdownItemProps,
  LoanOfficer,
  SubscriptionDetails
} from "../../../../../types"

interface Props {
  data: SubscriptionDetails
  loDetails: LoanOfficer
  handleChangeRateAlerts: (subscription: SubscriptionDetails) => void
  onChangeStatus: (subscription: SubscriptionDetails, status: string) => void
  onEdit?: () => void
}

const EngagementDetailsHeader: FC<Props> = ({
  data,
  loDetails,
  handleChangeRateAlerts,
  onChangeStatus,
  onEdit
}) => {
  const inTabletView = useInTabletView()
  const dispatch = useAppDispatch()
  const [status, setStatus] = useState<string>(getStatusID(data))
  const selectedStatusOption = STATUS_OPTIONS.find(so => so.id === status)
  const {
    data: thread,
    isFetching: isFetchingThread,
    isError: isErrorThread
  } = useGetThreadQuery(
    { subscription_id: data.id },
    { skip: !data.id || !loDetails.sms_allowed }
  )
  const [leadSmsFollowup, { isLoading: isSendingSms }] =
    useLeadSmsFollowupMutation()

  // Check if SMS has been sent
  const isSMSSent = useMemo(
    () => !!loDetails.sms_allowed && !isFetchingThread && !isErrorThread,
    [loDetails.sms_allowed, isFetchingThread, isErrorThread]
  )
  const showSendSMS = useMemo(
    () => !!loDetails.sms_allowed && !isFetchingThread && !isSMSSent,
    [loDetails.sms_allowed, isFetchingThread, isSMSSent]
  )

  const handleViewDashboard = () => openInNewTab(data.dashboard_link)
  const handleCopyDashboardLink = () => copyToClipboard(data.dashboard_link)
  const handleChangeStatus = (id: string) => {
    if (status === id) return
    setStatus(id)
    onChangeStatus(data, id)
  }
  const handleSendSMS = async () => {
    if (!data.lead_id) {
      dispatch(
        pushToast({
          msg: "No lead associated with this subscription",
          timeout: 3000,
          theme: "danger"
        })
      )
      return
    }

    const resp = await leadSmsFollowup({ lead_id: data.lead_id })
    const result = (resp as any)?.data as
      | { status: string; thread_id?: string; message?: string }
      | undefined

    if (result?.status === "success") {
      dispatch(
        pushToast({ msg: "SMS followup sent", timeout: 3000, theme: "success" })
      )
    } else {
      dispatch(
        pushToast({
          msg: result?.message || "Failed to send SMS followup",
          timeout: 4000,
          theme: "danger"
        })
      )
    }
  }

  const menuItems: DropdownItemProps[] = [
    {
      name: "Edit subscription",
      icon: Pencil,
      iconClassName: "text-neutral-400 mr-2",
      onClick: onEdit
    },
    {
      name: "Copy dashboard link",
      icon: ClipboardCopy,
      iconClassName: "text-neutral-400 mr-2",
      onClick: handleCopyDashboardLink
    }
  ]

  if (inTabletView) {
    const tabletItems: DropdownItemProps[] = [
      {
        renderItem: () => (
          <BBSwitch
            className="py-3 md:pb-0 md:px-4"
            enabled={data.active === "1"}
            setEnabled={() => handleChangeRateAlerts(data)}
            label="Rate alerts"
            switchPosition="left"
          />
        )
      },
      { type: "divider" }
    ]

    if (showSendSMS) {
      tabletItems.push({
        name: "Send SMS",
        icon: MessageSquare,
        iconClassName: "text-neutral-400 mr-2",
        onClick: handleSendSMS
      })
    }

    tabletItems.push({
      name: "View dashboard",
      icon: Eye,
      iconClassName: "text-neutral-400 mr-2",
      onClick: handleViewDashboard
    })

    menuItems.unshift(...tabletItems)
  }

  const fullName =
    [data.first_name, data.last_name].filter(Boolean).join(" ") || data.email

  return (
    <>
      <BreadCrumbs
        pages={[
          {
            name: "Engagements",
            href: "/engagements",
            current: false
          },
          {
            name: data.email,
            href: "#",
            current: true
          }
        ]}
      />

      {/* Identity row */}
      <div className="pt-6 pb-5 flex items-start gap-4">
        <ProfileLogo
          label={`${data.first_name.charAt(0)}${data.last_name.charAt(0)}`}
          className="w-14 h-14 shrink-0 text-lg"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-neutral-900 truncate">
              {fullName}
            </h1>
            <EngagementsStatus status={status} />
            <If
              condition={isSMSSent}
              then={
                <div className="flex items-center gap-1.5">
                  <Badge.Alt className="py-px">SMS sent</Badge.Alt>
                  {thread?._id && (
                    <LightBtn
                      href={`/conversations/sms/${thread._id}`}
                      className="text-xs h-5 py-0 px-1.5"
                    >
                      <MessageSquare className="h-3 w-3 mr-1" />
                      View
                    </LightBtn>
                  )}
                </div>
              }
            />
          </div>
          <div className="text-sm text-neutral-500 mt-0.5">
            {data.email}
            <span className="mx-1.5">&middot;</span>
            {loDetails.bank}
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="pb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Listbox value={status} onChange={handleChangeStatus}>
            <div className="relative">
              <Listbox.Button className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 hover:bg-neutral-200 transition-colors h-9 px-3 text-sm font-medium focus:outline-none cursor-pointer">
                {selectedStatusOption?.icon && (
                  <span className="shrink-0">{selectedStatusOption.icon}</span>
                )}
                <span>{selectedStatusOption?.name ?? "Status"}</span>
                <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 w-40 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {STATUS_OPTIONS.map(option => (
                    <Listbox.Option
                      key={option.id}
                      value={option.id}
                      className={({ active }) =>
                        `relative select-none py-2 pl-8 pr-3 cursor-pointer ${
                          active
                            ? "bg-primary-500 text-white"
                            : "text-neutral-900"
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span className="flex items-center gap-2">
                            {option.icon && (
                              <span className="shrink-0">{option.icon}</span>
                            )}
                            <span
                              className={
                                selected ? "font-medium" : "font-normal"
                              }
                            >
                              {option.name}
                            </span>
                          </span>
                          {selected && (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-2 ${
                                active ? "text-white" : "text-primary-500"
                              }`}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          <BBSwitch
            className="hidden lg:flex"
            enabled={data.active === "1"}
            setEnabled={() => handleChangeRateAlerts(data)}
            label="Rate alerts"
          />
        </div>

        <div className="h-6 w-px bg-neutral-200 hidden lg:block" />

        <div className="flex items-center gap-2">
          <If
            isLoading={isFetchingThread}
            loaderSize={4}
            condition={showSendSMS}
            then={
              <LightBtn
                type="button"
                className="hidden lg:inline-flex text-sm h-9"
                onClick={handleSendSMS}
              >
                <If
                  isLoading={isSendingSms}
                  loaderSize={4}
                  loaderClassName="!p-0"
                  condition={!isSendingSms}
                  then={
                    <>
                      <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                      Send SMS
                    </>
                  }
                />
              </LightBtn>
            }
          />
          <LightBtn
            className="hidden lg:inline-flex text-sm h-9"
            disabled={!data.dashboard_link}
            href={data.dashboard_link}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Dashboard
          </LightBtn>
          <Dropdown
            menuBtn={
              <div className="inline-flex justify-center items-center py-1.5 px-3 border text-sm font-medium rounded-md focus:outline-none bg-neutral-100 hover:bg-neutral-200 transition-colors h-9">
                Actions <ChevronDown className="h-4 w-4 ml-1" />
              </div>
            }
            items={menuItems}
          />
        </div>
      </div>
    </>
  )
}

export default EngagementDetailsHeader
